<script setup lang="ts">
import type { Schemas } from "#shopware";
import type { CmsElementProductDescriptionReviews } from "@shopware-pwa/composables-next";
import {
  getTranslatedProperty,
  getProductName,
} from "@shopware-pwa/helpers-next";
import { useCmsTranslations } from "@shopware-pwa/composables-next";
import { computed, ref } from "vue";
import { defu } from "defu";
import { useProduct, useUser, useShopwareContext } from "#imports";

const props = defineProps<{
  content: CmsElementProductDescriptionReviews;
}>();

type Translations = {
  product: {
    description: string;
    reviews: string;
    messages: {
      reviewAdded: string;
    };
  };
};

let translations: Translations = {
  product: {
    description: "Description",
    reviews: "Reviews",
    messages: {
      reviewAdded: "Thank you for submitting your review",
    },
  },
};
translations = defu(useCmsTranslations(), translations) as Translations;

const currentTab = ref<number>(1);
const { product } = useProduct(props.content.data?.product);

const description = computed(() =>
  getTranslatedProperty(product.value, "description")
);

const toggleTabs = (tabNumber: number) => {
  currentTab.value = tabNumber;
};

const reviews: Ref<Schemas["ProductReview"][]> = ref([]);

const { apiClient } = useShopwareContext();
const { isLoggedIn } = useUser();
const reviewAdded = ref(false);

const fetchReviews = async () => {
  const reviewsResponse = await apiClient.invoke(
    "readProductReviews post /product/{productId}/reviews",
    {
      pathParams: { productId: product.value.id },
    }
  );
  reviews.value = reviewsResponse.data.elements || [];
};
const handleReviewAdded = () => {
  reviewAdded.value = true;
  fetchReviews();
};

onMounted(async () => {
  if (props.content.data?.reviews?.elements) {
    reviews.value = props.content.data.reviews.elements;
  }
});

const activeAccordion = ref<any | null>(null);

const toggleAccordion = (index: any) => {
  activeAccordion.value = activeAccordion.value === index ? null : index;
};

const groupedProperties = computed(() => {
  if (!product.value?.properties) return {};

  const groups: Record<
    string,
    {
      name: string;
      properties: { name: string; media: any | null }[];
      customFields?: any | null;
    }
  > = {};

  product.value.properties.forEach((item) => {
    const groupId = item.groupId;
    const groupName = item.group.name;
    const propertyName = item.name;
    const propertyMedia = item.media || null;
    const customFields = item.group.customFields || null;

    // Initialize group if not exists
    if (!groups[groupId]) {
      groups[groupId] = {
        name: groupName,
        properties: [],
        customFields,
      };
    }

    if (
      !groups[groupId].properties.some(
        (property) => property.name === propertyName
      )
    ) {
      groups[groupId].properties.push({
        name: propertyName,
        media: propertyMedia,
      });
    }
  });

  return groups;
});

const isLastVisibleSpec = (currentSpec: string) => {
  const specs = [
    "weight",
    "width",
    "height",
    "length",
    "releaseDate",
    "unit",
  ] as const;
  // Find the last visible spec by iterating from the end
  for (let i = specs.length - 1; i >= 0; i--) {
    if (product.value[specs[i] as keyof typeof product.value]) {
      return currentSpec === specs[i];
    }
  }
  return false;
};
</script>

<template>
  <div v-if="product" class="accordion-sample-2 px-4">
    <div class="container mx-auto relative py-12 sm:py-16">
      <div class="title mb-8 md:mb-14 text-left md:text-center">
        <h3 class="text-2xl md:text-3xl lg:text-4xl font-bold">
          {{ $t("productDetails.descriptiontitle") }}
        </h3>
      </div>

      <div class="accordion w-full mx-auto md:w-10/12">
        <!-- Accordion Item 1 -->
        <div
          class="accordion-item bg-light-grey rounded-2xl mb-4 shadow-md"
          v-if="description"
        >
          <button
            class="accordion-header flex justify-between items-center w-full py-5 px-6 text-base md:text-xl font-medium bg-gray-100 text-dark-bg focus:outline-none bg-transparent border-0"
            @click="toggleAccordion(0)"
          >
            <span class="font-bold text-chinese-black leading-normal">{{
              $t("productDetails.useAndCare")
            }}</span>
            <svg
              :class="{
                'rotate-180': activeAccordion === 0,
                'rotate-0': activeAccordion !== 0,
              }"
              class="w-2 h-2 transform transition-transform accordion-icon"
              viewBox="0 0 8 5"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3.51984 4.17398L0.368716 1.02288C0.325249 0.979409 0.291016 0.930875 0.266016 0.877275C0.241016 0.823658 0.228516 0.766217 0.228516 0.70495C0.228516 0.582417 0.269949 0.475959 0.352816 0.385576C0.435666 0.295193 0.544874 0.25 0.680441 0.25H7.31884C7.45441 0.25 7.56362 0.295626 7.64647 0.386876C7.72933 0.478143 7.77076 0.584609 7.77076 0.706276C7.77076 0.736709 7.72397 0.842309 7.63037 1.02308L4.47944 4.174C4.40701 4.24643 4.33213 4.29932 4.25482 4.33265C4.17748 4.36598 4.09242 4.38265 3.99964 4.38265C3.90686 4.38265 3.8218 4.36598 3.74447 4.33265C3.66715 4.29932 3.59227 4.24643 3.51984 4.17398Z"
                fill="#141414"
              />
            </svg>
          </button>

          <div
            class="accordion-content overflow-hidden us text-medium-grey bg-white rounded-2xl rounded-tr-none rounded-tl-none"
            :style="{
              maxHeight: activeAccordion === 0 ? 'var(--content-height)' : '0',
            }"
          >
            <div class="content-inner px-5 py-4">
              <div
                class="text-chinese-black font-normal leading-normal description-text"
                v-html="description"
              ></div>
            </div>
          </div>
        </div>

        <!-- Accordion Item 2 -->
        <div
          v-if="
            (product.weight ||
              product.width ||
              product.height ||
              product.length ||
              product.releaseDate ||
              product.unit) &&
            false
          "
          class="accordion-item bg-light-grey rounded-2xl mb-4 shadow-md accordion-specification"
        >
          <button
            class="accordion-header flex justify-between items-center w-full py-5 px-6 text-base md:text-xl font-medium bg-gray-100 text-dark-bg focus:outline-none bg-transparent border-0"
            @click="toggleAccordion(1)"
          >
            <span class="font-bold text-chinese-black leading-normal">{{
              $t("productDetails.specificationsTitle")
            }}</span>
            <svg
              :class="{
                'rotate-180': activeAccordion === 1,
                'rotate-0': activeAccordion !== 1,
              }"
              class="w-2 h-2 transform transition-transform accordion-icon"
              viewBox="0 0 8 5"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3.51984 4.17398L0.368716 1.02288C0.325249 0.979409 0.291016 0.930875 0.266016 0.877275C0.241016 0.823658 0.228516 0.766217 0.228516 0.70495C0.228516 0.582417 0.269949 0.475959 0.352816 0.385576C0.435666 0.295193 0.544874 0.25 0.680441 0.25H7.31884C7.45441 0.25 7.56362 0.295626 7.64647 0.386876C7.72933 0.478143 7.77076 0.584609 7.77076 0.706276C7.77076 0.736709 7.72397 0.842309 7.63037 1.02308L4.47944 4.174C4.40701 4.24643 4.33213 4.29932 4.25482 4.33265C4.17748 4.36598 4.09242 4.38265 3.99964 4.38265C3.90686 4.38265 3.8218 4.36598 3.74447 4.33265C3.66715 4.29932 3.59227 4.24643 3.51984 4.17398Z"
                fill="#141414"
              />
            </svg>
          </button>

          <div
            class="accordion-content overflow-hidden text-medium-grey bg-white rounded-2xl rounded-tr-none rounded-tl-none"
            :style="{
              maxHeight: activeAccordion === 1 ? 'var(--content-height)' : '0',
            }"
          >
            <div class="content-inner px-5 py-4">
              <ul class="specification">
                <!-- Weight -->
                <li
                  v-if="product.weight"
                  class="flex items-center justify-between py-4"
                  :class="{
                    'border-b border-platinum': !isLastVisibleSpec('weight'),
                  }"
                >
                  <p class="text-base font-bold text-chinese-black">
                    {{ $t("productDetails.specifications.weight") }}
                  </p>
                  <span class="text-base font-normal text-chinese-black">
                    {{ product.weight }}
                  </span>
                </li>

                <!-- Width -->
                <li
                  v-if="product.width"
                  class="flex items-center justify-between py-4"
                  :class="{
                    'border-b border-platinum': !isLastVisibleSpec('width'),
                  }"
                >
                  <p class="text-base font-bold text-chinese-black">
                    {{ $t("productDetails.specifications.width") }}
                  </p>
                  <span class="text-base font-normal text-chinese-black">
                    {{ product.width }}
                  </span>
                </li>

                <!-- Height -->
                <li
                  v-if="product.height"
                  class="flex items-center justify-between py-4"
                  :class="{
                    'border-b border-platinum': !isLastVisibleSpec('height'),
                  }"
                >
                  <p class="text-base font-bold text-chinese-black">
                    {{ $t("productDetails.specifications.height") }}
                  </p>
                  <span class="text-base font-normal text-chinese-black">
                    {{ product.height }}
                  </span>
                </li>

                <!-- Length -->
                <li
                  v-if="product.length"
                  class="flex items-center justify-between py-4"
                  :class="{
                    'border-b border-platinum': !isLastVisibleSpec('length'),
                  }"
                >
                  <p class="text-base font-bold text-chinese-black">
                    {{ $t("productDetails.specifications.length") }}
                  </p>
                  <span class="text-base font-normal text-chinese-black">
                    {{ product.length }}
                  </span>
                </li>

                <!-- Release Date -->
                <li
                  v-if="product.releaseDate"
                  class="flex items-center justify-between py-4"
                  :class="{
                    'border-b border-platinum':
                      !isLastVisibleSpec('releaseDate'),
                  }"
                >
                  <p class="text-base font-bold text-chinese-black">
                    {{ $t("productDetails.specifications.releaseDate") }}
                  </p>
                  <span class="text-base font-normal text-chinese-black">
                    {{ product.releaseDate }}
                  </span>
                </li>

                <!-- Unit -->
                <li
                  v-if="product.unit"
                  class="flex items-center justify-between py-4"
                  :class="{
                    'border-b border-platinum': !isLastVisibleSpec('unit'),
                  }"
                >
                  <p class="text-base font-bold text-chinese-black">
                    {{ $t("productDetails.specifications.unit") }}
                  </p>
                  <span class="text-base font-normal text-chinese-black">
                    {{ product.unit }}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- properties of the product -->

        <template v-for="(property, index) in groupedProperties" :key="index">
          <div
            class="accordion-item bg-light-grey rounded-2xl mb-4 shadow-md accordion-suitable"
            v-if="property.properties.length > 0"
          >
            <button
              class="accordion-header flex justify-between items-center w-full py-5 px-6 text-base md:text-xl font-medium bg-gray-100 text-dark-bg focus:outline-none bg-transparent border-0"
              @click="toggleAccordion(index)"
            >
              <span class="font-bold text-chinese-black leading-normal">
                {{ property.name }}
              </span>
              <svg
                :class="{
                  'rotate-180': activeAccordion === index,
                  'rotate-0': activeAccordion !== index,
                }"
                class="w-2 h-2 transform transition-transform accordion-icon"
                viewBox="0 0 8 5"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3.51984 4.17398L0.368716 1.02288C0.325249 0.979409 0.291016 0.930875 0.266016 0.877275C0.241016 0.823658 0.228516 0.766217 0.228516 0.70495C0.228516 0.582417 0.269949 0.475959 0.352816 0.385576C0.435666 0.295193 0.544874 0.25 0.680441 0.25H7.31884C7.45441 0.25 7.56362 0.295626 7.64647 0.386876C7.72933 0.478143 7.77076 0.584609 7.77076 0.706276C7.77076 0.736709 7.72397 0.842309 7.63037 1.02308L4.47944 4.174C4.40701 4.24643 4.33213 4.29932 4.25482 4.33265C4.17748 4.36598 4.09242 4.38265 3.99964 4.38265C3.90686 4.38265 3.8218 4.36598 3.74447 4.33265C3.66715 4.29932 3.59227 4.24643 3.51984 4.17398Z"
                  fill="#141414"
                />
              </svg>
            </button>
            <div
              class="accordion-content overflow-hidden text-medium-grey bg-white rounded-2xl rounded-tr-none rounded-tl-none"
              :style="{
                maxHeight:
                  activeAccordion === index ? 'var(--content-height)' : '0',
              }"
            >
              <div class="content-inner px-6 py-5">
                <ul
                  v-if="property.customFields?.custom_menu_grid"
                  class="flex justify-between items-center"
                >
                  <li
                    v-for="properties in property.properties"
                    class="text-center"
                  >
                    <figure>
                      <NuxtImg
                        v-if="properties.media?.url"
                        :src="properties.media?.url"
                        :alt="properties.media?.alt"
                        loading="eager"
                        :width="properties.media?.metaData.width"
                        :height="properties.media?.metaData.height"
                        format="webp"
                      />
                    </figure>
                    <span
                      class="text-[10px] font-bold text-charleston-green uppercase"
                      >{{ properties.name }}</span
                    >
                  </li>
                </ul>
                <div
                  v-else
                  class="wrap flex justify-between items-center flex-wrap gap-8"
                >
                  <div
                    v-for="properties in property.properties"
                    class="col-span-1 w-96"
                  >
                    <div class="inner-wrap flex items-center justify-between">
                      <div
                        class="icon w-[32px] h-[32px] flex justify-center items-center bg-red-bg rounded-full"
                      >
                        <figure class="mt-1">
                          <NuxtImg
                            v-if="properties.media?.url"
                            :src="properties.media?.url"
                            :alt="properties.media?.alt"
                            loading="eager"
                            :width="properties.media?.metaData.width"
                            :height="properties.media?.metaData.height"
                            format="webp"
                          />
                          <img
                            v-else
                            src="assets/icon/heart-white-icon.svg"
                            alt="heart icon"
                          />
                        </figure>
                      </div>
                      <div class="content w-[calc(100%-44px)]">
                        <p class="font-bold text-dark-bg">
                          {{ properties.name }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- reviews section -->
        <div
          class="accordion-item bg-light-grey rounded-2xl shadow-md accordion-specification"
          v-if="reviews.length > 0"
        >
          <button
            class="accordion-header flex justify-between items-center w-full py-5 px-6 text-base md:text-xl font-medium bg-gray-100 text-dark-bg focus:outline-none bg-transparent border-0"
            @click="toggleAccordion(4)"
          >
            <span class="font-bold text-chinese-black leading-normal"
              >{{ $t("productDetails.reviews") }} ({{ reviews.length }})</span
            >
            <svg
              :class="{
                'rotate-180': activeAccordion === 4,
                'rotate-0': activeAccordion !== 4,
              }"
              class="w-2 h-2 transform transition-transform accordion-icon"
              viewBox="0 0 8 5"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3.51984 4.17398L0.368716 1.02288C0.325249 0.979409 0.291016 0.930875 0.266016 0.877275C0.241016 0.823658 0.228516 0.766217 0.228516 0.70495C0.228516 0.582417 0.269949 0.475959 0.352816 0.385576C0.435666 0.295193 0.544874 0.25 0.680441 0.25H7.31884C7.45441 0.25 7.56362 0.295626 7.64647 0.386876C7.72933 0.478143 7.77076 0.584609 7.77076 0.706276C7.77076 0.736709 7.72397 0.842309 7.63037 1.02308L4.47944 4.174C4.40701 4.24643 4.33213 4.29932 4.25482 4.33265C4.17748 4.36598 4.09242 4.38265 3.99964 4.38265C3.90686 4.38265 3.8218 4.36598 3.74447 4.33265C3.66715 4.29932 3.59227 4.24643 3.51984 4.17398Z"
                fill="#141414"
              />
            </svg>
          </button>

          <div
            class="accordion-content overflow-hidden text-medium-grey bg-white rounded-2xl rounded-tr-none rounded-tl-none"
            :style="{
              maxHeight: activeAccordion === 4 ? 'var(--content-height)' : '0',
            }"
          >
            <div class="content-inner px-5 py-4">
              <ul class="specification">
                <!-- Weight -->
                <ProductReviews
                  :product="product"
                  :reviews="reviews"
                  class="mb-8"
                />
                <ProductReviewsForm
                  v-if="isLoggedIn && !reviewAdded"
                  :product-id="product.id"
                  @success="handleReviewAdded"
                />
                <div v-if="reviewAdded">
                  {{ translations.product.messages.reviewAdded }}
                </div>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.accordion-icon {
  transition: transform 0.3s ease;
}

.accordion-content {
  transition: max-height 0.8s ease-in-out;
  --content-height: 1000px; /* This is a default value, will be updated by JS */
}

.content-inner {
  transition: opacity 0.3s ease;
}

.description-text ::v-deep(p) {
  margin-top: 12px;
  margin-bottom: 12px;
}
.description-text ::v-deep(ul) {
  list-style: disc;
  padding-left: 20px;
}
</style>
